import React, { useEffect, useRef, useCallback, useState } from "react";
import styled from "styled-components/macro";
import { Notification } from "./components/Notification";
import SessionService from "./services/SessionService";
import differenceInMilliseconds from "date-fns/differenceInMilliseconds";
import { useSystem } from "./hooks/useSystem";

export const App = () => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [lastMilisecondsOfSession, setLastMilisecondsOfSession] = useState(0);
  const [notificationInterval, setNotificationInterval] = useState(0);

  const system = useSystem();

  useEffect(() => {
    const init = async () => {
      try {
        SessionService.prolongSession(system); // Assure that session is as fresh as possible
        const { lastMilisecondsOfSession, notificationInterval } = await SessionService.getConfig();
        setLastMilisecondsOfSession(lastMilisecondsOfSession);
        setNotificationInterval(notificationInterval);
      } catch (error) {
        setLastMilisecondsOfSession(180000);
        setNotificationInterval(1020000);
        SessionService.logWarn(error, "Couldn't get config");
      }
    };
    init();
  }, [setLastMilisecondsOfSession, setNotificationInterval, system]);

  const setTimer = useCallback(async () => {
    try {
      const { date, expired } = await SessionService.getSessionDuration(system);
      if (!expired) {
        const difference = Math.max(0, differenceInMilliseconds(new Date(date), new Date()));
        clearTimeout(timerRef.current);
        if (difference <= lastMilisecondsOfSession) {
          SessionService.showIframe();
          timerRef.current = setTimeout(setTimer, difference + 1000);
        } else {
          timerRef.current = setTimeout(setTimer, difference - lastMilisecondsOfSession);
        }
      } else {
        SessionService.redirectToTiles();
      }
    } catch (error) {
      SessionService.logWarn(error, "Couldn't get session duration");
      SessionService.showIframe();
    }
  }, [lastMilisecondsOfSession, system]);

  const resetTimer = () => clearTimeout(timerRef.current);

  useEffect(() => {
    if (notificationInterval !== 0) {
      timerRef.current = setTimeout(setTimer, notificationInterval);
    }
    return () => clearTimeout(timerRef.current);
  }, [setTimer, notificationInterval]);

  return (
    <Wrapper>
      <Notification setTimer={setTimer} resetTimer={resetTimer} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  text-align: center;
`;
